import { useEffect, useState } from 'react';
import type { UpdateVaultDocumentInput, VaultDateGroup, VaultDocument } from '@accounting/shared';
import { VaultDocumentRow, needsAttention } from './VaultDocumentRow';

/**
 * The filing cabinet itself: Year → Month → documents, newest first.
 *
 * The most recent month is open on arrival; everything else starts closed so a
 * cabinet holding several years still fits on one screen. While a search is
 * running every month is opened, because a closed drawer hides the hit.
 */
export function VaultTree({
  companyId,
  groups,
  expandAll,
  onSave,
  onDelete,
  onRetry,
  saving,
}: {
  companyId: string;
  groups: VaultDateGroup[];
  expandAll: boolean;
  onSave: (id: string, input: UpdateVaultDocumentInput) => void;
  onDelete: (doc: VaultDocument) => void;
  onRetry: (id: string) => void;
  saving: boolean;
}) {
  const firstKey = groups[0]?.months[0]
    ? `${groups[0].year}-${groups[0].months[0].month}`
    : null;
  // Keys the user has flipped away from their default state. Default: the most
  // recent month is open, the rest are closed.
  const [toggled, setToggled] = useState<Set<string>>(new Set());

  // A new result set (a search, a different company) resets the manual state —
  // otherwise a drawer closed a minute ago hides today's hits.
  useEffect(() => setToggled(new Set()), [companyId, expandAll]);

  const isOpen = (key: string): boolean =>
    expandAll ? true : (key === firstKey) !== toggled.has(key);

  const toggle = (key: string) =>
    setToggled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {groups.map((group) => (
        <section key={group.year}>
          <h2 style={{ fontSize: 18, margin: '0 0 8px' }}>
            {group.year}{' '}
            <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>
              {group.months.reduce((n, m) => n + m.documents.length, 0)} documents
            </span>
          </h2>

          {group.months.map((month) => {
            const key = `${group.year}-${month.month}`;
            const open = isOpen(key);
            const flagged = month.documents.filter(needsAttention).length;
            return (
              <div key={key} className="card" style={{ padding: 0, marginBottom: 10, overflow: 'hidden' }}>
                <button
                  onClick={() => toggle(key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '10px 14px',
                    background: 'none',
                    border: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                    color: 'inherit',
                    textAlign: 'left',
                  }}
                  aria-expanded={open}
                >
                  <span style={{ width: 12 }}>{open ? '▾' : '▸'}</span>
                  <strong style={{ fontSize: 14 }}>
                    {month.month} {month.label}
                  </strong>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {month.documents.length} document{month.documents.length === 1 ? '' : 's'}
                  </span>
                  {flagged > 0 && <span className="tag warn">{flagged} need review</span>}
                </button>

                {open && (
                  <table className="table" style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 110 }}>Date</th>
                        <th>Document</th>
                        <th style={{ width: 130 }}>Type</th>
                        <th style={{ width: 170 }}>Vendor</th>
                        <th style={{ width: 110, textAlign: 'right' }}>Amount</th>
                        <th style={{ width: 110 }}>Status</th>
                        <th style={{ width: 70, textAlign: 'right' }}>Size</th>
                        <th style={{ width: 1 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {month.documents.map((doc) => (
                        <VaultDocumentRow
                          key={doc.id}
                          companyId={companyId}
                          doc={doc}
                          onSave={onSave}
                          onDelete={onDelete}
                          onRetry={onRetry}
                          saving={saving}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}
